'use client';

import React, { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    step: '01',
    icon: '📝',
    title: 'Enter Your Information',
    description: 'Fill in your real work experience, education, skills, and achievements. CareerLift never invents information — only you know your story.',
    color: 'bg-blue-600',
  },
  {
    step: '02',
    icon: '🎨',
    title: 'Choose a Template',
    description: 'Select from ATS-friendly resume templates. Switch templates at any time without losing your content.',
    color: 'bg-blue-500',
  },
  {
    step: '03',
    icon: '🤖',
    title: 'Get AI Suggestions',
    description: 'AI analyzes your existing content and suggests improvements — stronger language, better keywords, clearer structure. No fabrication.',
    color: 'bg-blue-700',
  },
  {
    step: '04',
    icon: '📊',
    title: 'Run ATS Analysis',
    description: 'Paste a job description to see how your resume matches. Get keyword gaps, formatting tips, and actionable improvements.',
    color: 'bg-blue-600',
  },
  {
    step: '05',
    icon: '📥',
    title: 'Export & Apply',
    description: 'Download your finished resume as a clean, properly formatted PDF ready to submit to employers.',
    color: 'bg-blue-500',
  },
];

function StepCard({ step, delay }: { step: typeof STEPS[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="flex gap-4"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-20px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      <div className="flex flex-col items-center shrink-0">
        <div className={`w-10 h-10 rounded-full ${step.color} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
          {step.step}
        </div>
        <div className="w-0.5 flex-1 bg-blue-100 mt-2 last:hidden" />
      </div>
      <div className="pb-8 flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">{step.icon}</span>
          <h3 className="text-base font-bold text-slate-800">{step.title}</h3>
        </div>
        <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
      </div>
    </div>
  );
}

export default function HowItWorksSection() {
  return (
    <section className="py-20 px-4 sm:px-6 bg-white" id="how-it-works">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Steps */}
          <div>
            <div className="mb-8">
              <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 bg-blue-50 text-blue-600 border border-blue-200">
                How It Works
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
                A resume builder that{' '}
                <span className="text-blue-600">actually works</span>
              </h2>
              <p className="mt-3 text-slate-500 text-sm sm:text-base">
                Five simple steps from blank page to polished PDF.
              </p>
            </div>
            <div>
              {STEPS.map((step, i) => <StepCard key={step.step} step={step} delay={i * 100} />)}
            </div>
          </div>

          {/* Right: What AI does / doesn't do */}
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-white rounded-2xl border border-blue-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-slate-800 mb-4">What the AI actually does</h3>
              <div className="space-y-3">
                {[
                  { label: 'Improves your existing bullet points', can: true },
                  { label: 'Strengthens grammar & clarity', can: true },
                  { label: 'Suggests keywords from job descriptions', can: true },
                  { label: 'Identifies ATS keyword gaps', can: true },
                  { label: 'Invents jobs or degrees for you', can: false },
                  { label: 'Fabricates metrics or achievements', can: false },
                  { label: 'Guarantees ATS approval or interviews', can: false },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3 text-sm">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs shrink-0 font-bold ${
                      item.can ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-red-50 text-red-500 border border-red-200'
                    }`}>
                      {item.can ? '✓' : '✗'}
                    </span>
                    <span className={item.can ? 'text-slate-700' : 'text-slate-400'}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Diff example */}
            <div className="bg-white rounded-2xl border border-blue-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-slate-800 mb-1">AI Improvement Example</h3>
              <p className="text-xs text-slate-400 mb-4">AI only rewrites what you provide</p>
              <div className="space-y-2">
                <div className="rounded-lg p-3 bg-red-50 border border-red-200">
                  <span className="text-xs font-semibold text-red-600 block mb-1">Before</span>
                  <p className="text-xs text-slate-600">Helped with product launches and worked with different teams</p>
                </div>
                <div className="rounded-lg p-3 bg-green-50 border border-green-200">
                  <span className="text-xs font-semibold text-green-600 block mb-1">After (AI improved)</span>
                  <p className="text-xs text-slate-600">Led cross-functional product launches — AI improved clarity using your own words</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
